import useApiService from 'services/api'
import confirm from 'components/dialogConfirm'
import BaseSelectModal, {
  BaseSelectModalProps,
} from './BaseSelectModal/BaseSelectModal'
import { bulkEditSingleParams } from 'services/apiTypes'
import {
  buildCampaignSelectItems,
  CampaignSelectItem,
  CLEAR_ORIGIN_CAMPAIGN_VALUE,
} from './campaignSelectItems'

/**
 * Bulk "Set Origin Campaign" modal for the deals tables (FIR-7148).
 *
 * Single-select clone of the Sales Stage / Account Owner modals: pick a campaign
 * to attribute selected deals to, or "No campaign" to clear it (gated behind a
 * confirmation). A vertical mismatch is rejected by the backend with a 400 whose
 * message is surfaced as a toast. Routes to the deals or reports bulk endpoint
 * via `useReportsEndpoint`.
 */
export default function CampaignSelectModal({
  tableObjAccessor = 'origin_campaign',
  useReportsEndpoint = false,
  ...props
}: {
  tableObjAccessor?: string
  useReportsEndpoint?: boolean
} & Omit<
  BaseSelectModalProps<CampaignSelectItem>,
  | 'apiAction'
  | 'title'
  | 'subtitle'
  | 'label'
  | 'placeholder'
  | 'items'
  | 'tableObjAccessor'
>) {
  const api = useApiService()
  // No type filter: deals on the table can span verticals, so offer every
  // campaign. The backend rejects an incompatible campaign with a 400 that the
  // modal surfaces as a toast.
  const { data: campaignOptions } = api.useGetCampaignsOptions()

  const apiAction = async (params: bulkEditSingleParams) => {
    const { item_id, ...rest } = params

    // The "No campaign" option carries the clear sentinel; a bare Apply with no
    // selection also resolves to null. Both clear the origin campaign and are
    // gated behind a confirmation. Returning early cancels the mutation, so
    // BaseSelectModal rolls back the optimistic update and keeps the modal open.
    const isClear = item_id === null || item_id === CLEAR_ORIGIN_CAMPAIGN_VALUE
    const origin_campaign_id = isClear ? null : item_id

    if (isClear) {
      const confirmed = await confirm(
        'Remove the origin campaign from the selected deals? Companies already assigned to this campaign will stay assigned.',
        'Clear origin campaign',
        '',
        { confirmLabel: 'Clear', buttonVariant: 'danger' }
      )
      if (!confirmed) return
    }

    return useReportsEndpoint
      ? api.bulkEditDealReports({ deal: { origin_campaign_id }, ...rest })
      : api.bulkEditDeal({ deal: { origin_campaign_id }, ...rest })
  }

  if (!campaignOptions) return null

  const items = buildCampaignSelectItems(campaignOptions)

  return (
    <BaseSelectModal
      {...props}
      title="Set Origin Campaign"
      subtitle={
        <span>
          Set or clear the origin campaign for <b>all</b> selected rows.
        </span>
      }
      label="Campaign"
      placeholder="Select campaign"
      items={items}
      tableObjAccessor={tableObjAccessor!}
      apiAction={apiAction}
    />
  )
}
